import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Activity, Waves, Shield, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await api.login(email, password);
      login(res.access_token, res.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden hero-wash flex-col justify-center px-16">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Voice Care Monitor</h1>
              <p className="text-sm text-muted-foreground">Healthcare Call Quality Intelligence</p>
            </div>
          </div>
          
          <div className="space-y-6 max-w-md">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Waves className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h3 className="font-medium text-sm">Real-time Audio Transcription</h3>
                <p className="text-xs text-muted-foreground mt-1">AI-powered speech-to-text with speaker diarization for every call</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Activity className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <h3 className="font-medium text-sm">AI-Powered Quality Analysis</h3>
                <p className="text-xs text-muted-foreground mt-1">Automated scoring for grammar, professionalism, empathy, and resolution</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-medium text-sm">Secure & Enterprise-Grade</h3>
                <p className="text-xs text-muted-foreground mt-1">Role-based access control with admin, supervisor, and agent views</p>
              </div>
            </div>
          </div>

          <blockquote className="mt-12 border-l-2 border-primary/30 pl-4">
            <p className="text-sm italic text-muted-foreground">"Transforming healthcare call quality with cutting-edge AI technology."</p>
            <cite className="text-xs text-muted-foreground mt-1 block">- Voice Care Team</cite>
          </blockquote>
        </div>
      </div>

      {/* Right panel - Login */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-xl font-bold">Voice Care Monitor</h1>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold">Welcome Back</h2>
              <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email or Agent Name</label>
                <Input
                  type="text"
                  placeholder="admin@voicecare.com or Agent Name"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Password</label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-background"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>

            <div className="text-xs text-center text-muted-foreground mt-4 space-y-1">
              {/* <p>Admin: admin@voicecare.com / admin123</p> */}
              <p>Agents: Use your agent name as both username &amp; password</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
